import { memo, useEffect, useSyncExternalStore, type MouseEvent, type ReactElement } from "react";
import { logical } from "../../ui/kit/index.ts";
import { ABILITY_SLOT } from "../bar/layout.ts";
import type { TipAnchor, TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import type { StanceStore } from "../fight/stance.ts";
import { slotRect } from "./area-slots.ts";
import type { HudArea } from "./slots.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { say } from "../../words.ts";
import { slotArt } from "./slot-art.ts";
import { SLOT_ICONS } from "./slot-icons.ts";

/**
 * The stance button (P2.2-T13, ADR-073's amendment "the stance: a weapon put away", item 6): the weapon drawn or
 * sheathed, in its slot as the area stands it (`slotRect`), a gutter right of the ability bar, drawn as a slot is, with
 * its key at the top left while the viewer's Show key numbers on the bar is on (P2.18-T15; the setting covers every key
 * the bar draws), and the `sword` glyph drawn or the `sheathed` glyph sheathed, the latter in the muted colour, so the
 * shape carries the state and not the colour alone. A click or a tap toggles it through the stance store, as R does; it
 * is a toggle button, pressed while drawn. Its explanation is the HUD's tooltip (`stance-words.ts`), an anchor like a
 * slot's: a hover, keyboard focus, or a long press that pins it open, whose click is swallowed so a long press never
 * toggles. The design has no stance affordance, so the look is a draft built from the slot and glyph system.
 */

/**
 * What the button reads: the stance, a way to hear it change, and the toggle.
 */
export type StanceSource = Pick<StanceStore, "subscribe" | "getSnapshot" | "toggle">;

/**
 * The button's tooltip anchor.
 */
export const STANCE_ANCHOR: TipAnchor = { surface: "stance", index: 0 };

/**
 * The stance icon's box in the slot: the 16 pixel art centred inside the slot's ring (`slot-art.ts`).
 */
const art = slotArt();

/**
 * Keeps a pointer press on the button from taking focus, so a button left focused never answers Space or Enter, which
 * the game captures, as a slot does.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The stance button in its slot.
 *
 * @param props The `area` it is laid out on, the stance's `source`, the tooltip's controller (`tips`, null where no
 *   tooltip shows), whether it draws its key (`showKey`, the viewer's Show key numbers on the bar; the tooltip names the
 *   key either way), and `onCommit`, told after each commit.
 * @returns The button.
 */
export const StanceButton = memo(function StanceButton({ area, source, tips, showKey, onCommit }: { readonly area: HudArea; readonly source: StanceSource; readonly tips: TipController | null; readonly showKey: boolean; readonly onCommit: (() => void) | undefined }): ReactElement {
  useEffect(() => {
    onCommit?.();
  });
  const stance = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const handlers = useTipHandlers(tips);
  const key = useKeyLabel("stance");
  const rect = slotRect("stance", area);
  const drawn = stance === "drawn";
  return (
    <div className="ot-hud-slot" data-hud-slot="stance" {...handlers} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <button
        type="button"
        className={`ot-hud-ability ot-hud-stance is-${stance}`}
        data-hud="stance"
        data-stance={stance}
        data-tip={`${STANCE_ANCHOR.surface}:${STANCE_ANCHOR.index}`}
        aria-pressed={drawn}
        aria-label={say("hud.stance.button")}
        onMouseDown={keepFocus}
        onClick={() => {
          if (tips?.click(STANCE_ANCHOR) !== true) {
            source.toggle();
          }
        }}
        style={{ width: logical(ABILITY_SLOT.size), height: logical(ABILITY_SLOT.size) }}
      >
        <img className="ot-hud-stance__art" data-hud="stance-icon" src={drawn ? SLOT_ICONS.stanceDrawn : SLOT_ICONS.stanceSheathed} alt="" draggable={false} style={{ left: logical(art.left), top: logical(art.top), width: logical(art.size), height: logical(art.size) }} />
        {showKey ? (
          <span className="ot-hud-ability__key" aria-hidden="true">
            {key}
          </span>
        ) : null}
      </button>
    </div>
  );
});
